import { Star, MapPin, Phone, Calendar, ShieldCheck, Sparkles } from "lucide-react";
import { CLINIC } from "../lib/clinic";

export const Hero = () => {
  const go = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative pt-28 lg:pt-32 pb-16 lg:pb-24 overflow-hidden"
    >
      {/* Decorative background */}
      <div className="absolute inset-0 -z-10">
        <div className="absolute top-0 left-0 w-[520px] h-[520px] bg-[#009688]/8 rounded-full blur-3xl -translate-x-1/3 -translate-y-1/4" />
        <div className="absolute top-40 right-0 w-[420px] h-[420px] bg-[#4FC3F7]/12 rounded-full blur-3xl translate-x-1/3" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          {/* Left */}
          <div className="lg:col-span-6 order-2 lg:order-1">
            <div
              data-testid="hero-badge-rating"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-[#009688]/15 shadow-[0_4px_20px_rgba(0,0,0,0.04)]"
            >
              <div className="flex -space-x-0.5">
                {[1, 2, 3, 4, 5].map((i) => (
                  <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />
                ))}
              </div>
              <span className="text-sm font-semibold text-[#1F2937]">
                {CLINIC.rating}
              </span>
              <span className="text-sm text-[#1F2937]/60">
                · {CLINIC.reviewCount} Google Reviews
              </span>
            </div>

            <h1 className="mt-6 text-5xl sm:text-6xl lg:text-[76px] font-bold tracking-tight text-[#1F2937] leading-[1.02]">
              Your Smile
              <br />
              is Our{" "}
              <span className="relative inline-block">
                <span className="relative z-10 text-[#009688]">Priority</span>
                <svg
                  className="absolute -bottom-2 left-0 w-full"
                  viewBox="0 0 300 12"
                  preserveAspectRatio="none"
                >
                  <path
                    d="M2 8 Q 75 2 150 6 T 298 5"
                    stroke="#4FC3F7"
                    strokeWidth="4"
                    strokeLinecap="round"
                    fill="none"
                  />
                </svg>
              </span>
              .
            </h1>

            <p
              className="mt-6 text-lg text-[#1F2937]/70 leading-relaxed max-w-xl"
              data-testid="hero-subheading"
            >
              Providing high-quality dental care with modern technology and
              experienced professionals in the heart of Manjeri, Kerala.
            </p>

            <p className="mt-3 text-base font-malayalam text-[#009688] font-medium">
              {CLINIC.nameMl}
            </p>

            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <button
                data-testid="hero-book-btn"
                onClick={() => go("appointment")}
                className="group inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full bg-[#009688] text-white font-semibold text-[15px] hover:bg-[#00796b] active:scale-95 transition-all shadow-xl shadow-[#009688]/25"
              >
                <Calendar className="w-5 h-5" />
                Book Appointment
                <span className="ml-1 group-hover:translate-x-1 transition-transform">→</span>
              </button>
              <a
                href={`tel:${CLINIC.phoneRaw}`}
                data-testid="hero-call-btn"
                className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full bg-white border-2 border-[#009688]/20 text-[#1F2937] font-semibold text-[15px] hover:border-[#009688] hover:text-[#009688] active:scale-95 transition-all"
              >
                <Phone className="w-5 h-5" />
                Call Now
              </a>
            </div>

            {/* Stat strip */}
            <div className="mt-10 flex flex-wrap items-center gap-6 text-sm">
              <div className="flex items-center gap-2 text-[#1F2937]/70">
                <MapPin className="w-4 h-4 text-[#009688]" />
                Vellarangal, Manjeri
              </div>
              <div className="hidden sm:block w-px h-4 bg-[#1F2937]/15" />
              <div className="flex items-center gap-2 text-[#1F2937]/70">
                <ShieldCheck className="w-4 h-4 text-[#009688]" />
                Sterilized · Pain-Free
              </div>
              <div className="hidden sm:block w-px h-4 bg-[#1F2937]/15" />
              <div className="flex items-center gap-2 text-[#1F2937]/70">
                <Sparkles className="w-4 h-4 text-[#009688]" />
                Modern Equipment
              </div>
            </div>
          </div>

          {/* Right - Image */}
          <div className="lg:col-span-6 order-1 lg:order-2">
            <div className="relative">
              <div className="relative aspect-[4/5] max-w-[520px] mx-auto rounded-[36px] overflow-hidden shadow-[0_30px_80px_-20px_rgba(0,150,136,0.35)]">
                <img
                  src="https://images.unsplash.com/photo-1667133295315-820bb6481730?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80"
                  alt="Happy patient at Abhayam Dental Clinic"
                  loading="eager"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#009688]/25 via-transparent to-transparent" />
              </div>

              {/* Floating cards */}
              <div className="absolute -bottom-6 -left-4 lg:-left-8 glass rounded-2xl px-4 py-3 flex items-center gap-3 shadow-xl animate-floaty">
                <div className="w-11 h-11 rounded-xl bg-[#009688] grid place-items-center">
                  <ShieldCheck className="w-5 h-5 text-white" />
                </div>
                <div>
                  <div className="text-xs text-[#1F2937]/60 font-medium">Trusted by</div>
                  <div className="text-sm font-bold text-[#1F2937]">1,000+ Patients</div>
                </div>
              </div>

              <div
                className="absolute -top-4 -right-2 lg:-right-6 glass rounded-2xl px-4 py-3 shadow-xl animate-floaty"
                style={{ animationDelay: "1s" }}
              >
                <div className="flex items-center gap-2">
                  <div className="flex -space-x-1">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <Star key={i} className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
                    ))}
                  </div>
                  <div className="text-sm font-bold text-[#1F2937]">4.3</div>
                </div>
                <div className="text-[11px] text-[#1F2937]/60 font-medium mt-0.5">
                  Google Reviews
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
